import { styled } from '@f8n-frontend/stitches';
import { P, match } from 'ts-pattern';

import FrameGrid from 'components/FrameGrid';

import {
  mapStoryHeadingJsonToText,
  groupSizeToWidthMap,
  isTextStoryGroup,
  isStoryImageGroup,
  isStoryVideoGroup,
  isStoryVideoPopulated,
  appendQuotes,
  isStoryInventoryItemGroup,
} from 'utils/story/story';

import { StoryTextBlock, StoryGroup } from 'types/Story';

import { Story } from './StoryBase';
import { ContentControlButton } from './StoryContentBlock';
import { StoryImageBlock, StoryGroupGrid } from './StoryImageGroup';
import { StoryInventoryBlock } from './StoryNftGroup';
import { mapParagraphNodeToText } from './StoryParagraph';
import { StoryVideoBlockPopulated } from './StoryVideo';

function StoryEditorBlock(props: { block: StoryTextBlock }) {
  const block = props.block;

  return match(block)
    .with({ type: 'PARAGRAPH', content: P.select() }, (content) => (
      <Story.Paragraph as="div">
        {content.content.map((paragraph, key) => (
          <p key={key}>{paragraph.content.map(mapParagraphNodeToText)}</p>
        ))}
      </Story.Paragraph>
    ))
    .with({ type: 'H1', content: P.select() }, (content) => (
      <Story.H1>{mapStoryHeadingJsonToText(content)}</Story.H1>
    ))
    .with({ type: 'H2', content: P.select() }, (content) => (
      <Story.H2>{mapStoryHeadingJsonToText(content)}</Story.H2>
    ))
    .with({ type: 'BLOCKQUOTE', content: { content: P.select() } }, (content) =>
      content.map((item, index) => (
        <Story.Blockquote key={index}>
          {item.content.map((item, index) =>
            match(item)
              .with({ type: 'paragraph' }, (content) => (
                <p key={index}>{appendQuotes(content.content[0].text)}</p>
              ))
              .with({ type: 'footer' }, (content) => (
                <footer key={index}>
                  <cite>{content.content[0].content[0].text}</cite>
                </footer>
              ))
              .exhaustive()
          )}
        </Story.Blockquote>
      ))
    )
    .exhaustive();
}

export function StoryBody(props: { storyGroups: StoryGroup[] }) {
  return (
    <StoryBodyContainer>
      {props.storyGroups.map((group) => {
        if (isTextStoryGroup(group)) {
          return (
            <StoryGroupContainer
              key={group.id}
              style={{
                maxWidth: groupSizeToWidthMap[group.size],
              }}
            >
              {group.items.map((item) => (
                <StoryEditorBlock key={item.id} block={item} />
              ))}
            </StoryGroupContainer>
          );
        }

        if (isStoryImageGroup(group)) {
          return (
            <StoryGroupContainer
              key={group.id}
              style={{
                maxWidth: groupSizeToWidthMap[group.size],
              }}
            >
              <StoryGroupGrid
                css={{
                  gridTemplateColumns: '1fr',
                  '@bp0': {
                    gridTemplateColumns: `repeat(${group.items.length}, 1fr)`,
                  },
                }}
              >
                {group.items.map((item) => (
                  <StoryImageBlock key={item.id} item={item} />
                ))}
              </StoryGroupGrid>
            </StoryGroupContainer>
          );
        }

        if (isStoryVideoGroup(group)) {
          return (
            <StoryGroupContainer
              key={group.id}
              style={{
                maxWidth: groupSizeToWidthMap[group.size],
              }}
            >
              {group.items.filter(isStoryVideoPopulated).map((item) => {
                return (
                  <StoryVideoBlockPopulated
                    key={item.id}
                    src={item.content.src}
                    width={item.content.width}
                    height={item.content.height}
                  />
                );
              })}
            </StoryGroupContainer>
          );
        }

        if (isStoryInventoryItemGroup(group)) {
          return (
            <StoryGroupContainer
              key={group.id}
              style={{
                maxWidth: groupSizeToWidthMap[group.size],
              }}
            >
              <FrameGrid.Root maxColumns={group.items.length as 1 | 2}>
                {group.items.map((item) => (
                  <StoryInventoryBlock key={item.id} item={item} />
                ))}
              </FrameGrid.Root>
            </StoryGroupContainer>
          );
        }

        /**
         * TODO (storytelling): handle group types (e.g. NFT)
         */
        return null;
      })}
    </StoryBodyContainer>
  );
}

export const StoryBodyContainer = styled('div', {
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexGrow: 1,
  paddingTop: '$4',
  gap: '$3',

  '@bp0': {
    paddingTop: '$8',
    paddingX: '$7',
  },
});

export const StoryGroupContainer = styled('div', {
  marginX: 'auto',
  width: '100%',
  position: 'relative',
  borderRadius: '$4',
  boxSizing: 'border-box',

  paddingX: '$6',
  paddingY: '$3',

  '@bp0': {
    paddingY: '$6',
  },

  [`${ContentControlButton}`]: {
    opacity: 0,
    transition: 'opacity $2 $ease',
  },
  '@hover': {
    '&:hover': {
      [`${ContentControlButton}`]: {
        opacity: 1,
      },
    },
  },

  variants: {
    isFocused: {
      true: {
        backgroundColor: '$black2 !important',
        borderColor: '$black5 !important',
      },
    },
  },
});
